import React from "react";
import { Box } from "@mui/material";
import StepUpSpinner from "./StepUpSpinner";

/**
 * Branded page-content overlay loader. Same Step Up Academy logo + ring
 * as the full-screen <Loader />, but positioned as an absolute overlay
 * inside a `position: relative` parent so it covers a page section
 * (KPIs / filters / results) without hiding the app sidebar and header.
 *
 * Usage:
 *
 *   <Box sx={{ position: "relative" }}>
 *     ...page content...
 *     <PageOverlayLoader visible={isFetching} />
 *   </Box>
 *
 * a11y:
 *   role="status" + aria-live announce the load; the overlay itself
 *   also intercepts pointer events (pointerEvents: "auto") to block
 *   interaction with the stale content underneath while the request is
 *   pending.
 */
interface Props {
  /** Show/hide the overlay. Typically wired to `isFetching`. */
  visible: boolean;
  /** Optional label shown under the logo. Defaults to "Loading". */
  label?: string;
  /**
   * Spinner size. Default "medium" is a good fit for filter / pagination
   * refreshes; use "large" when it also acts as the initial-load loader,
   * "small" inside drawers/modals.
   */
  size?: "small" | "medium" | "large";
}

const PageOverlayLoader: React.FC<Props> = ({ visible, label = "Loading", size = "medium" }) => {
  if (!visible) return null;
  return (
    <Box
      role="status"
      aria-live="polite"
      aria-label={label}
      sx={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        // Near-white background per the reference — high enough opacity
        // to fully hide stale rows underneath, low enough to keep the
        // page layout perceptible for a smooth transition.
        bgcolor: "rgba(255, 255, 255, 0.92)",
        // Sit above every card / chip / dropdown that might otherwise
        // paint on top during a rerender, but below MUI modals (1300)
        // and Snackbars (1400) so the app's own dialogs still layer on top.
        zIndex: 1200,
        pointerEvents: "auto",
      }}
    >
      <StepUpSpinner size={size} label={label} />
    </Box>
  );
};

export default PageOverlayLoader;
